import { getApi } from 'api';
import { AxiosResponse } from 'axios';
import BlobResponseType from 'types/blob/BlobResponseType';
import {
  IApplicantDetail,
  IApplicantListParams,
  IApplicantListResponse,
  IPromoteApplicantResponse,
} from 'types/employee/IJobApplicant';

const BASE_URL = 'JobApplicants';

export const getApplicantsRequest = async (params: IApplicantListParams): Promise<IApplicantListResponse> => {
  const { companyId, statuses, name, pageIndex, pageSize, sortCriteria, sortOrder } = params;

  const query = new URLSearchParams();
  statuses.forEach(status => query.append('statuses', String(status)));
  query.append('pageIndex', String(pageIndex));
  query.append('pageSize', String(pageSize));
  query.append('sortCriteria', String(sortCriteria));
  query.append('sortOrder', String(sortOrder));
  if (name) query.append('name', name);

  const response: AxiosResponse<IApplicantListResponse> = await getApi().get(
    `${BASE_URL}/${companyId}/Applicants?${query.toString()}`,
  );
  return response.data;
};

export const getApplicantDetailRequest = async (companyId: number, id: number): Promise<IApplicantDetail> => {
  const response: AxiosResponse<IApplicantDetail> = await getApi().get(`${BASE_URL}/${companyId}/Applicants/${id}`);
  return response.data;
};

export const discardApplicantRequest = async (companyId: number, id: number): Promise<void> => {
  await getApi().post(`${BASE_URL}/${companyId}/Applicants/${id}/Discard`);
};

export const restoreApplicantRequest = async (companyId: number, id: number): Promise<void> => {
  await getApi().post(`${BASE_URL}/${companyId}/Applicants/${id}/Restore`);
};

/** PATCH /JobApplicants/{companyId}/Applicants/{id}/Locations. Wholesale-replaces the branch
 * set — it does not append. */
export const updateApplicantBranchesRequest = async (
  companyId: number,
  id: number,
  locationIds: number[],
): Promise<void> => {
  await getApi().patch(`${BASE_URL}/${companyId}/Applicants/${id}/Locations`, { locationIds });
};

export const promoteApplicantRequest = async (companyId: number, id: number): Promise<IPromoteApplicantResponse> => {
  const response: AxiosResponse<IPromoteApplicantResponse> = await getApi().post(
    `${BASE_URL}/${companyId}/Applicants/${id}/Promote`,
  );
  return response.data;
};

/** GET /JobApplicants/{companyId}/Applicants/{id}/Cv. Caller must have checked `cvFileName`
 * on the profile response first — a 404 here (APPLICANT_CV_NOT_FOUND) means calling this
 * unnecessarily for an applicant with no CV, which the UI should never do. */
export const downloadApplicantCvRequest = async (companyId: number, id: number): Promise<BlobResponseType> => {
  const response: AxiosResponse<number> = await getApi().get(`${BASE_URL}/${companyId}/Applicants/${id}/Cv`, {
    responseType: 'blob',
  });

  return {
    content: response.data,
    contentType: response.headers['content-type'],
  };
};
